// src/components/strumenti/CorrenteCortoCircuito.js
// Metodo convenzionale delle impedenze per il calcolo semplificato della Icc a fondo linea,
// dato il dato di targa/misura della Icc a monte (es. al quadro generale, fornito dal
// distributore o misurato) e l'impedenza del cavo verso valle. Considera solo la resistenza
// del cavo (non la reattanza) — approssimazione comune per sezioni non troppo grandi.
import React, { useState } from "react";
import { RefreshCw } from "lucide-react";
import { leggiNumero } from "../../utils/numeriStrumenti";

const inp = { padding:"10px 12px", border:"1px solid #e5e7eb", borderRadius:8, fontSize:14, fontFamily:"'Inter',sans-serif", outline:"none", width:"100%" };
const lbl = { fontSize:11, fontWeight:600, color:"#6b7280", display:"block", marginBottom:4 };
const RESISTIVITA = { rame: 0.0178, alluminio: 0.0286 };

export default function CorrenteCortoCircuito() {
  const [tipoGuasto, setTipoGuasto] = useState("trifase");
  const [tensione, setTensione] = useState("400");
  const [iccOrigine, setIccOrigine] = useState("");
  const [materiale, setMateriale] = useState("rame");
  const [sezione, setSezione] = useState("");
  const [lunghezza, setLunghezza] = useState("");

  // W442: nel guasto fase-neutro la tensione è quella di fase (230 V), anche in un sistema 400 V
  const V = tipoGuasto==="trifase" ? (leggiNumero(tensione)||0) : 230;
  const IccOrigineA = (leggiNumero(iccOrigine)||0)*1000; // kA -> A
  const S = leggiNumero(sezione)||0;
  const L = leggiNumero(lunghezza)||0;
  const rho = RESISTIVITA[materiale];

  const puoCalcolare = IccOrigineA>0 && S>0 && L>0;
  let zAmonte=null, zCavo=null, iccFondoLinea=null;

  if (puoCalcolare) {
    if (tipoGuasto==="trifase") {
      zAmonte = V/(Math.sqrt(3)*IccOrigineA);
      zCavo = (rho*L)/S;
      iccFondoLinea = V/(Math.sqrt(3)*(zAmonte+zCavo));
    } else {
      zAmonte = V/IccOrigineA;
      zCavo = (2*rho*L)/S; // andata + ritorno
      iccFondoLinea = V/(zAmonte+zCavo);
    }
  }

  return (
    <div>
      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10, marginBottom:12 }}>
        <div>
          <label style={lbl}>Tipo di guasto</label>
          <select value={tipoGuasto} onChange={e=>setTipoGuasto(e.target.value)} style={inp}>
            <option value="trifase">Trifase</option>
            <option value="monofase">Fase-Neutro (monofase)</option>
          </select>
        </div>
        <div>
          <label style={lbl}>{tipoGuasto==="trifase" ? "Tensione concatenata (V)" : "Tensione di fase (V)"}</label>
          {tipoGuasto!=="trifase" ? <div style={{ ...inp, background:"#f9fafb", color:"#374151" }}>230 V (fase-neutro)</div> :
          <select value={tensione} onChange={e=>setTensione(e.target.value)} style={inp}>
            <option value="400">400 V</option>
            <option value="230">230 V</option>
          </select>}
        </div>
      </div>

      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10, marginBottom:12 }}>
        <div>
          <label style={lbl}>Icc a monte (kA){tipoGuasto==="trifase" ? " trifase" : " fase-neutro"} — dato di targa/misura al quadro</label>
          <input value={iccOrigine} onChange={e=>setIccOrigine(e.target.value)} style={inp} placeholder="Es. 6"/>
        </div>
        <div>
          <label style={lbl}>Materiale conduttore</label>
          <select value={materiale} onChange={e=>setMateriale(e.target.value)} style={inp}>
            <option value="rame">Rame</option>
            <option value="alluminio">Alluminio</option>
          </select>
        </div>
      </div>
      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10, marginBottom:16 }}>
        <div>
          <label style={lbl}>Sezione cavo (mm²)</label>
          <input value={sezione} onChange={e=>setSezione(e.target.value)} style={inp} placeholder="Es. 6"/>
        </div>
        <div>
          <label style={lbl}>Lunghezza cavo (m)</label>
          <input value={lunghezza} onChange={e=>setLunghezza(e.target.value)} style={inp} placeholder="Es. 30"/>
        </div>
      </div>

      <div style={{ background:"#faf5ff", border:"1px solid #e9d5ff", borderRadius:10, padding:"14px 16px" }}>
        <div style={{ display:"flex", justifyContent:"space-around", marginBottom:10, fontSize:11, color:"#6b7280" }}>
          <div style={{textAlign:"center"}}>
            <div>Impedenza a monte</div>
            <div style={{fontWeight:700, color:"#374151", fontSize:14}}>{zAmonte!==null ? (zAmonte*1000).toFixed(2)+" mΩ" : "—"}</div>
          </div>
          <div style={{textAlign:"center"}}>
            <div>Impedenza cavo</div>
            <div style={{fontWeight:700, color:"#374151", fontSize:14}}>{zCavo!==null ? (zCavo*1000).toFixed(2)+" mΩ" : "—"}</div>
          </div>
        </div>
        <div style={{ textAlign:"center", borderTop:"1px solid #e9d5ff", paddingTop:10 }}>
          <div style={{ fontSize:11, color:"#9ca3af", fontWeight:600, marginBottom:2 }}>Corrente di corto circuito a fondo linea</div>
          <div style={{ fontSize:26, fontWeight:800, color:"#7c3aed" }}>
            {iccFondoLinea!==null ? (iccFondoLinea/1000).toLocaleString("it-IT",{maximumFractionDigits:3}) : "—"}
            <span style={{ fontSize:14, marginLeft:6, color:"#9ca3af" }}>kA</span>
          </div>
        </div>
      </div>
      <div style={{ fontSize:10, color:"#9ca3af", marginTop:8, textAlign:"center" }}>
        Metodo convenzionale semplificato (solo resistenza, non reattanza) — utile per una verifica rapida del potere di interruzione e della soglia magnetica dell'interruttore a valle. Non sostituisce uno studio di cortocircuito completo (IEC 60909).
      </div>

      <button onClick={()=>{setIccOrigine("");setSezione("");setLunghezza("");setTensione("400");setTipoGuasto("trifase");setMateriale("rame");}}
        style={{ marginTop:12, display:"flex", alignItems:"center", gap:6, fontSize:12, color:"#9ca3af", background:"none", border:"none", cursor:"pointer" }}>
        <RefreshCw size={12}/> Azzera
      </button>
    </div>
  );
}
